import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { ProductService } from 'src/app/core/api/product/product.service';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { CouponStatus } from 'src/app/shared/models/constants/coupon-status';
import { RoutesKeys } from 'src/app/utils/routes';
import { NotesInfoPopupComponent } from '../../organisms/notes-info-popup/notes-info-popup.component';
import { CouponCardService } from '../../../../core/services/coupon-card/coupon-card.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BusinessStatus } from 'src/app/shared/models/constants/business-status';
import { InputValidationError } from 'src/app/shared/errors/reward-service/reward-service-errors';
import { BusinessFrozenException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';

@Component({
  selector: 'app-activity-card',
  templateUrl: './activity-card.component.html',
  styleUrls: ['./activity-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * A card that populates activities feed list
 */
export class ActivityCardComponent implements OnInit, AfterContentChecked {
  @Input() id!: number;
  @Input() headerTitle?: string;
  @Input() picture?: string;
  @Input() expirationDate!: string;
  @Input() usedDate!: string;
  @Input() activityType?: 'coupon';
  @Input() businessId?: number;
  @Input() couponStatus?: CouponStatus;
  @Input() note?: any;
  @Input() businessStatus!: BusinessStatus;
  @Input() disabledAt?: string;
  @Input() timelineDisabled?: boolean;
  @ViewChild(NotesInfoPopupComponent) child?: NotesInfoPopupComponent;
  businessPointsInfo?: any;
  chipAttributes = {};
  showInfo = false;
  showNotes = false;
  originalNote = '';
  loadingBusinessInfo = false;
  private _isCouponVip = false;
  @Output() closeStatusEvent = new EventEmitter();
  @Input() viewOnly!: boolean;

  MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;

  readonly redeemedText = 'redeemed-on';
  readonly canceledText = 'canceled-on';
  readonly suspendedText = 'suspended-on';
  readonly noteSavedText = 'note-succesfully-saved';
  readonly errorText = 'something-wrong';
  readonly activeText = 'active-coupon-expires-on';

  /**
   * @constructor
   */
  constructor(
    public readonly rewardService: RewardService,
    public readonly productService: ProductService,
    public readonly translationService: TranslationService,
    public readonly snackbarService: SnackBarService,
    public readonly changeDetector: ChangeDetectorRef,
    public readonly router: Router,
    public readonly couponCardService: CouponCardService
  ) {}

  /**
   * ngAfterContentChecked
   */
  ngAfterContentChecked(): void {
    this.changeDetector.markForCheck();
  }

  /**
   * on init
   */
  ngOnInit() {
    this.usedDate = this.translationService.localizeDate(this.usedDate);
    this.disabledAt = this.translationService.localizeDate(this.disabledAt!);
  }

  /**
   * Whether this activity is far from expiration
   */
  get isActive() {
    return (
      this.couponStatus === CouponStatus.ACTIVE &&
      this.businessStatus !== BusinessStatus.DISABLED
    );
  }

  /**
   * Whether this activity has an expired date
   */
  get isExpired() {
    return this.couponStatus === CouponStatus.EXPIRED;
  }

  /**
   * Expiration text depends on activity type and expiration date
   */
  get expirationText() {
    if (this.isExpired) {
      return 'expired-on';
    } else {
      return 'expiring-on';
    }
  }

  /**
   * Expiring on text
   */
  get expiringOn() {
    if (this.calculateDaysToExpiration() === 1) {
      return 'expires-tomorrow';
    } else if (this.calculateDaysToExpiration() === 0) {
      return 'expires-today';
    } else {
      return 'active-coupon-expires-on';
    }
  }

  /**
   * Check card footer status
   */
  get statusCheck() {
    const currentStatus = this.getStatus();
    switch (currentStatus) {
      case CouponStatus.REDEEMED:
        return [this.redeemedText, this.usedDate];
      case CouponStatus.CANCELED:
        return [this.canceledText, this.usedDate];
      case CouponStatus.SUSPENDED:
        return [this.suspendedText, this.disabledAt];
      case CouponStatus.ACTIVE:
      default:
        return this.isExpired
          ? [this.expirationText, this.expirationDate]
          : [this.expiringOn, this.expirationDate];
    }
  }

  /**
   * Retrieves the correct status to show.
   * @return {CouponStatus}
   */
  getStatus(): CouponStatus {
    const isDisabled = this.businessStatus === BusinessStatus.DISABLED;
    if (isDisabled && this.couponStatus === CouponStatus.ACTIVE) {
      return CouponStatus.SUSPENDED;
    }
    return this.couponStatus!;
  }

  /**
   * condition for red
   */
  get isCanceled() {
    return this.couponStatus === CouponStatus.CANCELED;
  }

  /**
   * condition for dark blue
   */
  get isRedeemed() {
    return this.couponStatus === CouponStatus.REDEEMED;
  }

  /**
   * condition for orange
   */
  get isSuspended() {
    return (
      this.couponStatus === CouponStatus.ACTIVE &&
      this.businessStatus === BusinessStatus.DISABLED
    );
  }

  /**
   * Toggles show info
   */
  toggleInfo() {
    if (!this.showInfo && !this.businessPointsInfo) {
      this.getBusinessInfo();
    }
    this.showInfo = !this.showInfo;
    this.closeStatusEvent.emit();
    if (this.showNotes) {
      this.child?.emitCloseClick();
    }
  }

  /**
   * Get business information about point summary
   */
  getBusinessInfo() {
    if (!this.businessPointsInfo && !this.loadingBusinessInfo) {
      this.loadingBusinessInfo = true;
      this.rewardService.getBusinessInfo(this.businessId!, this.id).subscribe({
        next: (businessData) => {
          this.businessPointsInfo = businessData;
        },
        complete: () => {
          this.loadingBusinessInfo = false;
        },
        error: () => {
          this.snackbarService.error('unexpected-error');
          this.loadingBusinessInfo = false;
        },
      });
    }
  }

  /**
   * Turns off show info
   */
  closeInfo() {
    this.showInfo = false;
  }

  /**
   * Toggles show comment
   * @return {void}
   */
  toggleNotes(): void {
    this.showInfo = false;
    this.closeStatusEvent.emit();
    if (this.showNotes) {
      return this.child?.emitCloseClick();
    }
    this.showNotes = !this.showNotes;
  }

  /**
   * Turns off show comment
   */
  closeNotes() {
    this.originalNote = this.note;
    this.child?.form.patchValue({ note: this.originalNote });
    this.showNotes = false;
  }

  /**
   * @param {string} note
   * Save notes
   */
  saveNote(note: string) {
    if (note !== this.note) {
      this.child?.form.controls.note.disable();
      if (this.activityType === 'coupon') {
        this.rewardService.putNoteCoupon(this.id, note).subscribe({
          next: (coupon) => {
            this.showNotes = false;
            this.note = coupon.note;
            this.child?.form.controls.note.enable();
            this.translationService
              .translateString(this.noteSavedText)
              .then((string) => {
                this.snackbarService.success(string);
              });
          },
          error: (err) => {
            this.translationService
              .translateString(this.errorText)
              .then((string) => {
                this.snackbarService.error(string);
              });
            if (err instanceof InputValidationError) {
              this.translationService
                .translateString('invalid-notesDisallowed')
                .then((msg) => {
                  this.snackbarService.warning(msg);
                });
              return;
            } else if (err instanceof BusinessFrozenException) {
              this.translationService
                .translateString('business-is-frozen-exception')
                .then((msg) => {
                  this.snackbarService.error(msg);
                });
              return;
            }

            this.child?.form.controls.note.enable();
          },
        });
      }
    }
  }

  /**
   * Calculate how many days lasts to expiration date
   * @return {number}
   */
  calculateDaysToExpiration(): number {
    const startDate = new Date();
    const expiresDate = new Date(this.expirationDate);

    startDate.setHours(0, 0, 0);
    expiresDate.setHours(23, 59, 59);

    return Math.floor(
      (Date.UTC(
        expiresDate.getFullYear(),
        expiresDate.getMonth(),
        expiresDate.getDate()
      ) -
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate()
        )) /
        this.MILLISECONDS_IN_DAY
    );
  }

  /**
   * Returns expiration date
   * @return {number}
   */
  getExpirationDate() {
    const expiresDate = new Date(this.expirationDate);

    return Date.UTC(
      expiresDate.getFullYear(),
      expiresDate.getMonth(),
      expiresDate.getDate()
    );
  }

  /**
   * Routes to costumer detail page
   */
  openCustomerDetailPage() {
    this.router.navigate([RoutesKeys.CUSTOMER_DETAIL, this.businessId], {
      queryParams: { detail: 'summary' },
    });
  }

  /**
   * Checks if business name length is too big
   *
   * @param {string} businessName
   * @return {boolean}
   */
  maxNameLength(businessName?: string): boolean {
    if (businessName === undefined) {
      return false;
    }
    const maxLength = 30;
    return maxLength >= businessName.length;
  }

  /**
   * Decides if this couponVip is active
   * @return {boolean}
   */
  @Input()
  get isCouponVip(): boolean {
    return this._isCouponVip;
  }

  /**
   * Sets this couponVip as a active one by using an html attribute
   * @param {any} value
   */
  set isCouponVip(value: any) {
    this._isCouponVip = coerceBooleanProperty(value);
  }

  /**
   * Returns current language
   * @return {string}
   */
  getLanguege() {
    return this.translationService.getActiveLang();
  }
}
